import { useEffect, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { AlertTriangle, CalendarClock, CheckCircle, Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENCY_SYMBOLS = { gbp: '£', usd: '$', eur: '€' };

function formatWhen(dateStr, timeStr) {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00`);
  return `${format(d, 'EEE d MMM yyyy')} at ${timeStr}`;
}

function formatDeadline(iso) {
  if (!iso) return null;
  return format(new Date(iso), "EEE d MMM yyyy 'at' HH:mm");
}

/** What staff see about the deposit before confirming a move (decided by the server, not editable). */
function DepositOutcome({ preview }) {
  if (!preview) return null;
  const symbol = CURRENCY_SYMBOLS[preview.deposit_currency] || '£';
  const amount = `${symbol}${Number(preview.deposit_amount || 0).toFixed(2)}`;
  switch (preview.deposit_outcome) {
    case 'locked':
      return (
        <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-3 text-sm text-amber-900" data-testid="move-outcome-locked">
          <p className="flex items-center gap-2 font-semibold"><Lock className="h-4 w-4" /> Deposit kept: {amount} will be locked</p>
          <p className="mt-1">
            This move is inside the non-refundable period, so the deposit carries over as <strong>non-refundable</strong>.
            The customer cannot get it back by cancelling online, wherever the booking is moved.
          </p>
        </div>
      );
    case 'refundable':
      return (
        <div className="rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm text-blue-900" data-testid="move-outcome-refundable">
          <p className="flex items-center gap-2 font-semibold"><CheckCircle className="h-4 w-4" /> Deposit carried over: {amount} stays refundable</p>
          <p className="mt-1">
            The customer is still before their refund cut-off, so the deposit stays refundable. Their new cut-off will be{' '}
            <strong>{formatDeadline(preview.refund_deadline_after)}</strong>.
          </p>
        </div>
      );
    case 'non_refundable':
      return (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <p className="font-semibold">Deposit carried over: {amount} (already non-refundable under its deposit rule)</p>
        </div>
      );
    case 'pending':
      return (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          The deposit ({amount}) has not been paid yet. The payment link still works for the new date.
        </div>
      );
    default:
      return (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          No deposit on this booking.
        </div>
      );
  }
}

export default function MoveBookingDialog({ open, onOpenChange, booking, restaurantId, onMoved }) {
  const [date, setDate] = useState(null);
  const [time, setTime] = useState('');
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [customerAgreed, setCustomerAgreed] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDate(null);
    setTime('');
    setSlots([]);
    setPreview(null);
    setPreviewError(null);
    setCustomerAgreed(false);
    setNote('');
  }, [open, booking?.id]);

  const handleDateSelect = async (next) => {
    setDate(next);
    setTime('');
    setPreview(null);
    setPreviewError(null);
    if (!next || !restaurantId) {
      setSlots([]);
      return;
    }
    setLoadingSlots(true);
    try {
      const response = await axios.get(`${API}/availability/${restaurantId}`, {
        params: { date: format(next, 'yyyy-MM-dd'), party_size: booking?.party_size || 2 },
      });
      setSlots((response.data.slots || []).filter((slot) => slot.available_tables > 0));
    } catch (error) {
      toast.error('Failed to load availability');
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleTimeSelect = async (nextTime) => {
    setTime(nextTime);
    setPreview(null);
    setPreviewError(null);
    if (!booking || !date) return;
    setLoadingPreview(true);
    try {
      const response = await axios.post(`${API}/admin/bookings/${booking.id}/move`, {
        booking_date: format(date, 'yyyy-MM-dd'),
        booking_time: nextTime,
        preview: true,
      });
      setPreview(response.data);
    } catch (error) {
      setPreviewError(error.response?.data?.detail || 'Could not check this move');
    } finally {
      setLoadingPreview(false);
    }
  };

  const needsAgreement = Boolean(booking?.deposit_required);
  const canSubmit = Boolean(preview) && !saving && (!needsAgreement || customerAgreed);

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const response = await axios.post(`${API}/admin/bookings/${booking.id}/move`, {
        booking_date: format(date, 'yyyy-MM-dd'),
        booking_time: time,
        customer_agreed: customerAgreed,
        note: note.trim() || null,
      });
      const outcome = response.data.deposit_outcome;
      toast.success('Booking moved', {
        description:
          outcome === 'locked'
            ? 'Deposit carried over and locked (non-refundable).'
            : outcome === 'refundable'
              ? 'Deposit carried over and still refundable until the new cut-off.'
              : response.data.email_sent
                ? 'Customer has been emailed.'
                : undefined,
      });
      onOpenChange(false);
      onMoved?.(response.data.booking);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move booking');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CalendarClock className="h-5 w-5" /> Move booking
          </DialogTitle>
          <DialogDescription>
            {booking ? `${booking.customer_name}, ${booking.party_size} guests. Currently ${formatWhen(booking.booking_date, booking.booking_time)}` : ''}
          </DialogDescription>
        </DialogHeader>

        {booking?.deposit_locked && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <Lock className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p>This deposit is already locked from an earlier move. It stays non-refundable.</p>
          </div>
        )}

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>New date</Label>
            <Calendar
              mode="single"
              selected={date}
              onSelect={handleDateSelect}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              className="rounded-md border"
            />
          </div>

          {date && (
            <div className="space-y-2">
              <Label>New time for {format(date, 'EEE d MMM')}</Label>
              {loadingSlots ? (
                <div className="flex justify-center py-4"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : slots.length > 0 ? (
                <div className="grid grid-cols-4 gap-2 max-h-44 overflow-y-auto">
                  {slots.map((slot) => (
                    <Button
                      key={slot.time}
                      variant={time === slot.time ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleTimeSelect(slot.time)}
                      className="h-auto min-h-12 flex-col gap-0.5 py-1.5"
                      data-testid={`move-slot-${slot.time}`}
                    >
                      <span>{slot.time}</span>
                      <span className="text-[10px] opacity-80">{slot.available_tables}/{slot.total_tables || '-'} free</span>
                    </Button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No available times for this date</p>
              )}
            </div>
          )}

          {loadingPreview && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Checking deposit…</div>
          )}
          {previewError && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" /> {previewError}
            </div>
          )}

          {preview && (
            <>
              <div className="rounded-lg border p-3 text-sm">
                <p className="text-muted-foreground line-through">{formatWhen(preview.from.booking_date, preview.from.booking_time)}</p>
                <p className="font-semibold">{formatWhen(preview.to.booking_date, preview.to.booking_time)}</p>
                <p className="mt-1 text-xs text-muted-foreground">Table assignments are cleared. Assign tables again on the new day.</p>
              </div>
              <DepositOutcome preview={preview} />

              {needsAgreement && (
                <label className="flex items-start gap-3 rounded-lg border border-slate-300 bg-white p-3 text-sm cursor-pointer">
                  <Checkbox
                    checked={customerAgreed}
                    onCheckedChange={(v) => setCustomerAgreed(Boolean(v))}
                    data-testid="move-customer-agreed"
                  />
                  <span>
                    The customer has agreed to the new date and time, and has been told what happens to their deposit
                    {preview.deposit_outcome === 'locked' ? ' (it is now non-refundable).' : '.'}
                  </span>
                </label>
              )}

              <div className="space-y-1">
                <Label htmlFor="move-note">Note (optional, saved on the booking)</Label>
                <Textarea
                  id="move-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Customer called, couldn't make original date"
                  rows={2}
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={!canSubmit} data-testid="move-confirm">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CalendarClock className="h-4 w-4 mr-2" />}
            Move booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
